import { useState, useEffect } from 'react';
import {
  MapPin, ArrowLeft, Zap, Wifi, Pencil, X, Check, QrCode, Plus,
  KeyRound, Copy, RefreshCw, Trash2, Loader2, Settings, ChevronDown, TimerOff,
} from 'lucide-react';
import {
  fetchLocation, fetchChargers, updateLocation, fetchTariffs,
  regenerateLocationJoinCode, clearLocationJoinCode, fetchLocationJoinCodeUsage,
  deleteLocation, getLocationDeleteImpact,
  getDisplayErrorMessage,
  type LocationDetail, type Charger, type Location, type Tariff,
} from '@/lib/api';
import {
  TOU_WINDOW_MAX_CENTS,
  ceilingInUnit, ceilingLabel, ceilingMessageFor, flatRateMaxCents, isCentsOverCeiling, isRateTextOverCeiling,
  joinFlatRateUnit, rateTextToCents, rateTextToStoredCents, splitFlatRateUnit, storedCentsToRateText,
  tariffHasRateOverCeiling, type FlatRateUnit,
} from '@/lib/siteElectricityInput';
import { needsRateConfirmStep } from '@/lib/siteElectricityInput';
import { RateConfirmStep } from '@/components/RateConfirmStep';
import { normalizeLatLng } from '@/lib/coords';
import formStyles from '@/components/ui/Form.module.css';
import { QrBulkModal } from '@/components/ui/QrBulkModal';
import { Modal } from '@/components/ui/Modal';
import { LocationAddChargerForm } from '@/components/ui/LocationAddChargerForm';
import { LocationResidentManagedCard } from '@/components/ui/LocationResidentManagedCard';
import { LocationFobRegistrationCard } from '@/components/ui/LocationFobRegistrationCard';
import { FacilitiesContactsCard } from '@/components/ui/FacilitiesContactsCard';
import { BuildingStaffInvitesCard } from '@/components/ui/BuildingStaffInvitesCard';
import { ChargerNetworkCard } from '@/components/ui/ChargerNetworkCard';
import { useAuthStore } from '@/store/auth';
import { LocationGooglePlaceIdCard } from '@/components/ui/LocationGooglePlaceIdCard';
import { LocationPlatformShareCard } from '@/components/ui/LocationPlatformShareCard';
import { LegacyRevenueShareCard } from '@/components/ui/LegacyRevenueShareCard';
import { LocationHostShareCard } from '@/components/ui/LocationHostShareCard';
import { SiteHostStatement } from '@/components/ui/SiteHostStatement';
import { LocationBankCard } from '@/pages/site/LocationBankCard';
import { LocationTransitionNoticeCard } from '@/pages/site/LocationTransitionNoticeCard';
import { BuildingCircuitsSection } from './BuildingCircuitsSection';
import { TariffAssignmentCard } from '@/components/tariffs/TariffAssignmentCard';
import { TimeWindowRows } from '@/components/ui/TimeWindowRows';
import { LocationTouPicker } from '@/components/LocationTouPicker';
import { rateNeedsTimeZone, suggestedTimeZone, timeZoneOptions, timeZoneSaveState } from '@/lib/siteTimeZone';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useLiveReload } from '@/components/LiveUpdatesProvider';
import { useStartTimeouts, formatTimeoutSummary, lastChargeLabel, siblingCableLabel } from '@/hooks/useStartTimeouts';
import { TimeoutCell } from '@/components/ui/TimeoutCell';
import { ResponsiveTable } from '@/components/ui/ResponsiveTable';
import { ScopedSessionsList } from '@/components/sessions/ScopedSessionsList';
import { InstallRecord } from '@/components/partner/InstallRecord';
import { LocationServiceCard } from '@/components/partner/LocationServiceCard';
import styles from './LocationDetailPage.module.css';
import { Switch } from '@/components/ui/Switch';

interface LocationDetailPageProps {
  locationId: string;
  onNavigate: (path: string) => void;
  /** /locations/:id/facilities, the link the alert emails carry: open
   *  scrolled to the facilities contacts. */
  focus?: 'facilities';
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  residential: 'Residential',
  multifamily: 'Multi-Family',
  commercial: 'Commercial',
  public: 'Public',
  fleet: 'Fleet',
};

export function LocationDetailPage({ locationId, onNavigate, focus }: LocationDetailPageProps) {
  const authUser = useAuthStore((st) => st.user);
  // Two-Minute Window 4.5 — this site's app-start timeout rate (30d)
  // and its chargers' rows, from the shared dashboard read.
  const timeoutsQ = useStartTimeouts('30d');
  const siteTimeouts = timeoutsQ.data?.locations.find((r) => r.location_id === locationId);
  const timeoutByCharger = new Map(
    (timeoutsQ.data?.chargers ?? []).map((r) => [r.charger_id, r]),
  );
  const [location, setLocation] = useState<LocationDetail | null>(null);
  // Bumped when a building staff invite is revoked: the contacts card reloads.
  const [contactsVersion, setContactsVersion] = useState(0);
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [bulkQrOpen, setBulkQrOpen] = useState(false);
  const [addChargerOpen, setAddChargerOpen] = useState(false);
  // Location Settings group is collapsed by default so the page leads with
  // the chargers roster; the per-Location admin controls tuck away below.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Detail-page recents: empty label until data resolves, then
  // the location name. Hook re-fires on label change.
  useRecentRoute(location ? `${location.name} (Site)` : '');

  // super_admin views locations cross-tenant (the CMS flow), so route its two
  // reads through the tenant-scoped admin endpoints in cms-provisioning.ts.
  // Every other role keeps the caller-scoped reads unchanged; the response
  // shapes are identical, so the render below is the same either way.

  // `silent` refetches without flipping the full-page spinner, so adding a
  // charger refreshes the roster and the stat in place instead of blanking
  // the whole page back to "Loading…".
  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([
      // One code path for every tier: the shared routes are dashboard-
      // scoped, and super_admin resolves cross-tenant there (2026-09-19).
      fetchLocation(locationId),
      fetchChargers({ location_id: locationId, limit: 50 }),
    ])
      .then(([loc, chargersRes]) => {
        setLocation(loc);
        setChargers(chargersRes.data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // `load` intentionally reads locationId — re-running it is the whole point of the effect.
  useEffect(() => { load(); }, [locationId]);

  // Live (28 Oct 2026): a charger here flipping status or a session here
  // changing refreshes the roster and the stats in place.
  useLiveReload({
    kinds: ['charger', 'session'],
    match: (e) => e.location_id === locationId,
    reload: () => load(true),
  });

  if (loading) {
    return <div className={styles.page}><p className={styles.loading}>Loading...</p></div>;
  }

  if (error || !location) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error || 'Location not found'}</div>
        <button className={styles.backBtn} onClick={() => onNavigate('/locations')}>
          <ArrowLeft size={16} /> Back to Sites
        </button>
      </div>
    );
  }

  // Delete (deactivate) this location. Preflight first so the confirm states
  // the cascade (chargers + circuits deactivated) and the blocked case (a child
  // charger mid-session) is surfaced before any write.
  const handleDelete = async (): Promise<void> => {
    try {
      const pre = await getLocationDeleteImpact(locationId);
      if (pre.blocked) {
        alert(pre.block?.reason ?? 'This location cannot be deleted right now.');
        return;
      }
      const im = pre.impact;
      const msg = [
        `Delete location "${im?.name ?? location.name}"?`,
        '',
        'This deactivates the location (soft delete -- history is kept) and:',
        `  • deactivates ${im?.chargers_to_deactivate ?? 0} charger(s) and ${im?.circuits_to_deactivate ?? 0} circuit(s) here`,
        `  • ${im?.sessions_preserved ?? 0} past session(s) are preserved`,
        '',
        'Continue?',
      ].join('\n');
      if (!confirm(msg)) return;
      await deleteLocation(locationId);
      onNavigate('/locations');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete location.');
    }
  };

  return (
    <div className={styles.page}>
      {/* Back link + header */}
      <button className={styles.backBtn} onClick={() => onNavigate('/locations')}>
        <ArrowLeft size={16} /> Back to Sites
      </button>

      <div className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.headerIcon}>
            <MapPin size={18} />
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.title}>{location.name}</h1>
            <p className={styles.subtitle}>
              {location.address.street}, {location.address.city}, {location.address.state} {location.address.zip}
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
        <span className={styles.typeBadge}>{LOCATION_TYPE_LABELS[location.type] ?? location.type}</span>
        <button
          className={styles.editToggleBtn}
          onClick={() => setBulkQrOpen(true)}
          disabled={chargers.length === 0}
          title={chargers.length === 0
            ? 'No chargers at this location yet'
            : 'Download QR codes for every charger here'}
        >
          <QrCode size={13} /> QR Codes
        </button>
        <button
          className={styles.editToggleBtn}
          onClick={() => { setEditing((v) => !v); setEditErr(''); }}
          title={editing ? 'Cancel edit' : 'Edit location'}
        >
          {editing ? <><X size={13} /> Cancel</> : <><Pencil size={13} /> Edit</>}
        </button>
        <button
          className={styles.editToggleBtn}
          style={{ color: '#dc2626' }}
          onClick={handleDelete}
          title="Delete (deactivate) this location"
        >
          <Trash2 size={13} /> Delete
        </button>
        </div>
      </div>

      {editing && (
        <LocationEditForm
          location={location}
          saving={saving}
          error={editErr}
          onCancel={() => { setEditing(false); setEditErr(''); }}
          onSave={async (patch) => {
            setSaving(true);
            setEditErr('');
            try {
              await updateLocation(location._id, patch);
              await load();
              setEditing(false);
            } catch (e) {
              // Surface server-side VALIDATION_ERROR messages verbatim
              // (e.g. "electricity_tou_windows have overlapping intervals
              // on Mon"). Non-validation errors fall back to a generic
              // copy — see getDisplayErrorMessage for the rationale.
              setEditErr(await getDisplayErrorMessage(e, 'Save failed. Please try again.'));
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {/* Stats cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <Zap size={16} className={styles.statIcon} />
          <div>
            <p className={styles.statLabel}>Total Chargers</p>
            <p className={styles.statValue}>{location.charger_count}</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <Wifi size={16} className={styles.statIcon} />
          <div>
            <p className={styles.statLabel}>Online</p>
            <p className={styles.statValue}>{location.online_chargers}</p>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statusDot} ${location.is_active ? styles.active : styles.inactive}`} />
          <div>
            <p className={styles.statLabel}>Status</p>
            <p className={styles.statValue}>{location.is_active ? 'Active' : 'Inactive'}</p>
          </div>
        </div>
        {/* Two-Minute Window 4.5 — starts the chargers accepted where
            nobody plugged in before the plug-in window closed. Site
            total; flagged above 40% of 10+ starts. */}
        <div
          className={styles.statCard}
          title={siteTimeouts
            ? [siblingCableLabel(siteTimeouts), lastChargeLabel(siteTimeouts.last_charged_at), siteTimeouts.offline_charger_count > 0 ? `${siteTimeouts.offline_charger_count} charger(s) offline` : ''].filter(Boolean).join(' · ')
            : undefined}
        >
          <TimerOff size={16} className={styles.statIcon} style={siteTimeouts?.flagged ? { color: '#ff3b30' } : undefined} />
          <div>
            <p className={styles.statLabel}>Start timeouts (30d)</p>
            <p className={styles.statValue} style={siteTimeouts?.flagged ? { color: '#ff3b30' } : undefined}>
              {siteTimeouts ? formatTimeoutSummary(siteTimeouts) : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Pricing (Warren, 2026-10-25): the default tariff every charger here
          without one of its own bills, what bills now, and a warning when
          it is missing or inactive. Edit is offered on location:write, never
          to a Site; its own route and audit row. */}
      <TariffAssignmentCard kind="location" id={location._id} onNavigate={onNavigate} />

      {/* The installer's job-site record, per charger: shown only to EVI and this site's own admins. */}
      <InstallRecord locationId={locationId} />
      {/* Chargers list — promoted above the per-Location admin
          cards (join code, resident-managed, Place ID) so the
          page leads with what operators actually scan for. The
          admin controls form a single grouped tail after the
          roster. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Chargers at this Location</h2>
          <button
            type="button"
            className={styles.editToggleBtn}
            onClick={() => setAddChargerOpen(true)}
          >
            <Plus size={13} /> Add charger
          </button>
        </div>
        {chargers.length === 0 ? (
          <p className={styles.empty}>No chargers registered at this location.</p>
        ) : (
          <ResponsiveTable<Charger>
            rows={chargers}
            rowKey={(ch) => ch._id}
            onRowClick={(ch) => onNavigate(`/chargers/${ch._id}`)}
            columns={[
              { key: 'identity', header: 'Identity', mobile: 'primary', cell: (ch) => <span className={styles.identity}>{ch.identity}</span> },
              { key: 'model', header: 'Vendor / Model', mobile: 'secondary', cell: (ch) => `${ch.vendor} ${ch.model}` },
              { key: 'connectors', header: 'Connectors', cell: (ch) => ch.connectors.length },
              { key: 'status', header: 'Status', cell: (ch) => (
                <span className={`${styles.onlineBadge} ${ch.is_online ? styles.online : styles.offline}`}>
                  {ch.is_online ? 'Online' : 'Offline'}
                </span>
              ) },
              { key: 'timeouts', header: 'Timeouts (30d)', cell: (ch) => <TimeoutCell row={timeoutByCharger.get(ch._id)} /> },
              { key: 'heartbeat', header: 'Last Heartbeat', cell: (ch) => (
                <span className={styles.dimText}>{ch.last_heartbeat ? new Date(ch.last_heartbeat).toLocaleString() : '—'}</span>
              ) },
            ]}
          />
        )}
      </div>

      {/* Facilities contacts (Warren, 28 Oct 2026, item 6): the people EVI
          contacts about this location. Outside the collapsed settings so a
          link (/locations/:id/facilities) lands on it. */}
      <FacilitiesContactsCard key={contactsVersion} locationId={location._id} focus={focus === 'facilities'} />

      {/* Invite building staff (Warren, 28 Oct 2026, item 5): Super Admin, the
          site's operator or its owner; hidden for anyone else. */}
      <BuildingStaffInvitesCard locationId={location._id} onChanged={() => setContactsVersion((v) => v + 1)} />

      {/* Link, SIM state, breaker and panel, per charger (Warren, 4 Nov 2026, The
          Vered). Renders only when the API says this viewer may: EVI staff. */}
      <ChargerNetworkCard locationId={location._id} />

      {/* This site's sessions with who started each one, failed starts
          included (item 9, 2026-10-23). */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Sessions at this Location</h2>
        </div>
        <ScopedSessionsList
          locationId={location._id}
          chargerLabels={new Map(chargers.map((ch) => [ch._id, ch.identity]))}
          onNavigate={onNavigate}
        />
      </div>

      {/* Building circuits (DLM unit 4) — manage this building's dynamic
          load management circuits and their member chargers. Forked from
          the fleet Circuits tab; the route's tenant scope gates who can
          mutate (tenant admin own building, super_admin any). */}
      <BuildingCircuitsSection locationId={location._id} />
      <LocationBankCard key={`bank-${location._id}`} locationId={location._id} />
      {/* Super Admin: the Site Host Terms Section 13 Transition Notice. It changes what the bank card above says. */}
      {authUser?.role === 'super_admin' && <LocationTransitionNoticeCard key={`transition-${location._id}`} locationId={location._id} />}

      {/* Service stays at the tail of the page, immediately before Location Settings. */}
      <LocationServiceCard key={`service-${locationId}`} locationId={locationId} />

      {/* Location Settings — the per-Location admin controls (resident-managed
          toggle, building join code, Google Place ID, and for super_admin the
          EVI platform share) grouped into one
          collapsible, collapsed by default so the page leads with the chargers
          roster above. BuildingCircuitsSection stays outside this group. */}
      <section className={styles.settingsSection}>
        <button
          type="button"
          className={styles.settingsHeader}
          onClick={() => setSettingsOpen((v) => !v)}
          aria-expanded={settingsOpen}
        >
          <span className={styles.settingsTitleGroup}>
            <Settings size={15} />
            <span className={styles.settingsTitle}>Location Settings</span>
          </span>
          <ChevronDown
            size={16}
            className={`${styles.settingsChevron} ${settingsOpen ? styles.settingsChevronOpen : ''}`}
          />
        </button>

        {settingsOpen && (
          <div className={styles.settingsBody}>
            {/* Per-Location resident-managed-chargers toggle. Phase 3 of
                the tenant→location migration — replaces the global
                card that used to render in the Locations page header.
                Default value `true` from Phase 1 schema + backfill;
                flipping to false makes the PWA add-charger gate at
                chargers/index.ts return 403 with code
                LOCATION_DISABLED_RESIDENT_REGISTRATION for new
                registrations at this Location. */}
            <LocationResidentManagedCard
              locationId={location._id}
              value={location.allow_resident_managed_chargers ?? true}
              onChange={(next) => {
                setLocation((prev) => prev
                  ? { ...prev, allow_resident_managed_chargers: next }
                  : prev,
                );
              }}
            />

            {/* Per-building fob self-registration (F3). Opt-IN, so absent
                reads as off - the API gate reads === true. */}
            <LocationFobRegistrationCard
              locationId={location._id}
              locationTenantId={location.tenant_id}
              value={location.allow_resident_fob_registration === true}
              onChange={(next) => {
                setLocation((prev) => prev
                  ? { ...prev, allow_resident_fob_registration: next }
                  : prev,
                );
              }}
            />

            {/* Per-Location join code card. Shipped alongside the
                Tenant-tier code on SettingsPage; both coexist for the
                commercial-operator + multifamily cases. Not for sales
                partners: the API strips join_code for them (F4 #10), so
                the card would claim "no code set" for a building that has
                one. */}
            {authUser?.template !== 'sales_agent' && <LocationJoinCodeCard
              locationId={location._id}
              locationTenantId={location.tenant_id}
              initialJoinCode={location.join_code ?? null}
              initialGeneratedAt={location.join_code_generated_at ?? null}
              onChanged={(code, generatedAt) => {
                // Keep the local cached LocationDetail in sync so a
                // subsequent edit form doesn't snapshot stale code data.
                setLocation((prev) => prev
                  ? { ...prev, join_code: code, join_code_generated_at: generatedAt }
                  : prev,
                );
              }}
            />}

            {/* Google Maps Place ID — Phase 2 of the gamification
                rules update. Optional admin-paste field; when set,
                Phase 3's post-session "Leave a Google Review" CTA
                on the PWA uses the value as the deep-link target.
                Empty/unset means the CTA is hidden for this Location. */}
            <LocationGooglePlaceIdCard
              locationId={location._id}
              value={location.google_place_id}
              onChange={(next) => {
                setLocation((prev) => prev
                  ? { ...prev, google_place_id: next || undefined }
                  : prev,
                );
              }}
            />

            {/* EVI platform share (Location.revenue_split), Tier 2.
                super_admin only, the same signal the API checks: PUT
                /locations/:id 403s any other caller that sends the key.
                A stored share below 15% is flagged in the card, never
                auto-fixed. */}
            {authUser?.role === 'super_admin' && (
              <LocationPlatformShareCard
                locationId={location._id}
                value={location.revenue_split}
                onChange={(next) => {
                  setLocation((prev) => prev
                    ? { ...prev, revenue_split: next }
                    : prev,
                  );
                }}
              />
            )}
            {authUser?.role==='super_admin'&&<><LegacyRevenueShareCard locationId={location._id} /><LocationHostShareCard locationId={location._id}/></>}
          </div>
        )}
      </section>

      <SiteHostStatement locationId={location._id}/>
      {bulkQrOpen && (
        <QrBulkModal
          chargers={chargers}
          locationId={location._id}
          locationName={location.name}
          onClose={() => setBulkQrOpen(false)}
        />
      )}

      <Modal
        isOpen={addChargerOpen}
        onClose={() => setAddChargerOpen(false)}
        title="Register Charger"
        size="lg"
      >
        <LocationAddChargerForm
          locationId={location._id}
          onSuccess={() => { setAddChargerOpen(false); load(true); }}
          onCancel={() => setAddChargerOpen(false)}
        />
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Inline edit form
// ══════════════════════════════════════════════════════════════════════════

interface LocationEditFormProps {
  location: LocationDetail;
  saving: boolean;
  error: string;
  onCancel: () => void;
  onSave: (patch: Partial<{
    name: string;
    address: Location['address'];
    type: string;
    is_active: boolean;
    is_visible_on_driver_map: boolean;
    photo_url: string | null;
    subtitle: string | null;
    electricity_rate_cents: number;
    electricity_rate_type: 'per_kwh' | 'per_hour';
    electricity_tariff_id: string | null;
    electricity_tou_windows: ElectricityTouWindowAdmin[] | null;
    /** The second step's answer for a flat rate above 40 cents per kWh (Warren, 21 October 2026, night). */
    confirm_above_40: boolean;
  }>) => Promise<void>;
}

/** Inline TOU window shape carried through the admin UI + API
 *  patch payload. Distinct from Tariff's TOU window only by the
 *  `rate_cents` field name — Location-side reimbursement is a
 *  building cost, Tariff-side is a customer-facing energy rate. */
interface ElectricityTouWindowAdmin {
  day_of_week: number[];
  start_hour: number;
  end_hour: number;
  rate_cents: number;
  /** Per-window unit. 'per_kwh' (legacy default) bills
   *  energy_in_window × rate; 'per_hour' bills duration_in_window ×
   *  rate. Server defaults to per_kwh when the field is omitted, so
   *  pre-Commit-B docs round-trip unchanged. */
  rate_type: 'per_kwh' | 'per_hour';
}

type ReimbursementMode = 'tariff' | 'inline_tou' | 'flat';

/** Pick the active mode from a Location's persisted shape. Mirrors
 *  the server-side classifier in routes/locations/index.ts. */
function modeFromLocation(loc: LocationDetail): ReimbursementMode {
  if (loc.electricity_tariff_id) return 'tariff';
  if (Array.isArray(loc.electricity_tou_windows) && loc.electricity_tou_windows.length > 0) {
    return 'inline_tou';
  }
  return 'flat';
}

function LocationEditForm({ location, saving, error, onCancel, onSave }: LocationEditFormProps) {
  const [name, setName] = useState(location.name);
  const [street, setStreet] = useState(location.address.street);
  const [city, setCity] = useState(location.address.city);
  const [state, setState] = useState(location.address.state);
  const [zip, setZip] = useState(location.address.zip);
  const [country, setCountry] = useState(location.address.country ?? 'US');
  const [type, setType] = useState(location.type);
  const [isActive, setIsActive] = useState(location.is_active);
  // `is_visible_on_driver_map` is distinct from `is_active`: active is
  // the operational state (is this location in the fleet at all),
  // visibility is the discovery flag (do drivers see it on their map).
  // Default to true when the backend hasn't supplied a value yet so
  // legacy docs round-trip as visible.
  const [isVisibleOnDriverMap, setIsVisibleOnDriverMap] = useState(
    location.is_visible_on_driver_map ?? true,
  );
  // Photo + subtitle are orthogonal to the operational flags: photo
  // drives the StationHero fallback chain (admin URL → Street View →
  // gradient), subtitle adds the two-tier Novelle-style eyebrow.
  // Empty string → null on submit so the backend stores a clean sentinel
  // (StationHero treats null and "" identically, but null reads better
  // in the DB and in audit logs).
  const [photoUrl, setPhotoUrl] = useState(location.photo_url ?? '');
  const [subtitle, setSubtitle] = useState(location.subtitle ?? '');

  // Three-mode Electricity Reimbursement state. The active mode
  // governs which fields persist on save — the unselected modes
  // get nulled out so the server-side resolver (tariff > inline
  // TOU > flat) reads unambiguously.
  const [reimbursementMode, setReimbursementMode] = useState<ReimbursementMode>(
    modeFromLocation(location),
  );
  // Each mode's draft state lives independently so the user can
  // tab between modes without losing their in-progress input until
  // they hit Save.
  // Commit D — unit selector for the FLAT branch. TOU windows
  // already have per-window rate_type (Commit B); this drives the
  // single flat rate above. Defaults to per_kwh on existing
  // Locations so behavior is unchanged.
  const [electricityRateType, setElectricityRateType] = useState<'per_kwh' | 'per_hour'>(
    location.electricity_rate_type ?? 'per_kwh',
  );
  // Display unit for the flat-rate input. Storage stays in cents
  // (electricity_rate_cents); this state drives whether the
  // operator types raw cents ("18") or dollars ("0.18", "0.4500").
  // Default chosen by magnitude — rates ≥100¢ ($1+) read more
  // naturally in dollars (commercial per_kwh, idle-fee per_hour);
  // sub-$1 rates stay in cents (typical residential per_kwh).
  type FlatDisplayUnit = 'cents' | 'dollars';
  const [flatDisplayUnit, setFlatDisplayUnit] = useState<FlatDisplayUnit>(() => {
    const c = location.electricity_rate_cents ?? 0;
    return c >= 100 ? 'dollars' : 'cents';
  });
  // Input string in the CURRENT display unit. Re-renders when the
  // unit toggles (handler below converts the value to match).
  // Shown as stored, to a hundredth of a cent (Warren, 21 October 2026,
  // night: "so 43.63 stays 43.63"): "43.63" in ¢, "0.4363" in $.
  const [electricityRateCents, setElectricityRateCents] = useState<string>(() => {
    const c = location.electricity_rate_cents;
    if (c == null || c === 0) return '';
    return (c >= 100 ? 'dollars' : 'cents') === 'dollars'
      ? (c / 100).toFixed(4)
      : storedCentsToRateText(c, 'cents');
  });
  // Combined "(unit) × (rate_type)" dropdown value. Encodes both
  // dimensions in a single select element — operators think about
  // the unit as one choice ("$ per kWh", "¢ per hour", etc.),
  // splitting into two dropdowns reads heavier than the gain.
  const flatCombinedUnit: FlatRateUnit = joinFlatRateUnit(flatDisplayUnit, electricityRateType);
  const handleFlatUnitChange = (next: FlatRateUnit): void => {
    const { display: nextDisplay, rateType: nextRateType } = splitFlatRateUnit(next);
    // Re-render the input value in the new display unit when the
    // cents↔dollars dimension changes. Parse current text in old
    // unit, scale, re-format in new unit. rate_type change alone
    // (kwh↔hour) doesn't touch the value — that's a meaning change
    // not a magnitude change.
    if (nextDisplay !== flatDisplayUnit) {
      // Convert through canonical cents.
      const cents = rateTextToCents(electricityRateCents, flatDisplayUnit);
      if (cents !== null) {
        // To a hundredth of a cent either way: $0.4363 reads 43.63¢, not 44.
        setElectricityRateCents(
          nextDisplay === 'dollars' ? (cents / 100).toFixed(4) : storedCentsToRateText(cents, 'cents'),
        );
      }
    }
    setFlatDisplayUnit(nextDisplay);
    setElectricityRateType(nextRateType);
  };
  const [electricityTariffId, setElectricityTariffId] = useState<string>(
    location.electricity_tariff_id ?? '',
  );
  const [inlineTouWindows, setInlineTouWindows] = useState<ElectricityTouWindowAdmin[]>(
    () => (location.electricity_tou_windows ?? []).map((w) => ({
      day_of_week: [...w.day_of_week],
      start_hour: w.start_hour,
      end_hour: w.end_hour,
      rate_cents: w.rate_cents,
      // Backfill legacy docs (pre-Commit-B) to the per_kwh default
      // matching the schema default. The Location detail response
      // hydrates the field, but a doc written before the field
      // existed yields undefined — fall through to per_kwh.
      rate_type: w.rate_type ?? 'per_kwh',
    })),
  );

  // Tariff dropdown options — fetched lazily on first render of the
  // edit form so closed views don't pay the round-trip. Empty array
  // when fetch fails (the dropdown still renders with the current
  // value as a fallback so the form doesn't blank out a configured
  // tariff visually).
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  useEffect(() => {
    fetchTariffs().then(setTariffs).catch(() => setTariffs([]));
  }, []);
  const selectedTariff = tariffs.find((t) => t._id === electricityTariffId) ?? null;

  // Time zone (Site Host Terms 3.4): time-of-use periods are read in the
  // location's local time, and the API refuses a time-of-use rate without a
  // place zone. Sent only when changed, so an unrelated edit of a location
  // with an old stored value is never blocked by it.
  const [timezone, setTimezone] = useState(location.timezone ?? '');
  const zoneSuggestion = suggestedTimeZone({ state, country, coordinates: location.address.coordinates ?? null });
  const needsZone = rateNeedsTimeZone({ mode: reimbursementMode, inlineWindows: inlineTouWindows.length, tariff: selectedTariff });
  // Same rule as the API: only a save that changes the rate settings or the
  // zone is refused; anything else is allowed with a warning.
  const [initialRateKey] = useState(() => JSON.stringify([modeFromLocation(location), location.electricity_tariff_id ?? '',
    (location.electricity_tou_windows ?? []).map((w) => [w.day_of_week, w.start_hour, w.end_hour, w.rate_cents, w.rate_type ?? 'per_kwh'])]));
  const rateKey = JSON.stringify([reimbursementMode, reimbursementMode === 'tariff' ? electricityTariffId.trim() : (location.electricity_tariff_id ?? ''),
    (reimbursementMode === 'inline_tou' ? inlineTouWindows : (location.electricity_tou_windows ?? []))
      .map((w) => [w.day_of_week, w.start_hour, w.end_hour, w.rate_cents, w.rate_type ?? 'per_kwh'])]);
  const zoneState = timeZoneSaveState({ needsZone, zone: timezone, rateChanged: rateKey !== initialRateKey,
    zoneChanged: timezone !== (location.timezone ?? '') });
  const zoneMissing = zoneState === 'block';

  // Scale by display unit before storing as canonical cents, to a
  // hundredth of a cent (Warren, 21 October 2026, night: "43.63 stays
  // 43.63"). Dollars input ("0.4363") → 43.63 cents; cents input
  // ("43.63") → 43.63 cents. Blank / NaN / negative stores 0. Rounding
  // happens last so a dollars input like 0.43635 doesn't round before
  // the *100 scaling (@/lib/siteElectricityInput).
  const flatRateCents = rateTextToStoredCents(electricityRateCents, flatDisplayUnit);

  // The Site electricity ceilings, split by shape (Warren 2026-09-27):
  // a flat rate caps at 60¢ per kWh (above 40 with the second step,
  // Warren, 21 October 2026, night) or 80¢ per hour (its unit select
  // decides), every inline TOU window at 80¢, and a bound tariff by its
  // own rate_type (base and tiers 40¢, TOU rows 80¢; all 80¢ per hour).
  // The API refuses any rate above its cap, so each input says so inline
  // and Save holds until it is fixed. The flat rate ships in every mode
  // (it is the resolver's floor), so it is checked in every mode. It is
  // checked on the typed value before rounding, so it agrees with the
  // input's native max: 60.4¢/kWh is refused, not stored as 60.
  const flatMaxCents = flatRateMaxCents(electricityRateType);
  const flatOverCeiling = isRateTextOverCeiling(electricityRateCents, flatDisplayUnit, flatMaxCents);
  const touOverCeiling = reimbursementMode === 'inline_tou'
    && inlineTouWindows.some((w) => isCentsOverCeiling(w.rate_cents, TOU_WINDOW_MAX_CENTS));
  const tariffOverRate = reimbursementMode === 'tariff' && selectedTariff !== null
    ? tariffHasRateOverCeiling(selectedTariff)
    : null;
  const tariffOverCeiling = tariffOverRate !== null;
  const overCeiling = flatOverCeiling || touOverCeiling || tariffOverCeiling;
  // Warren, 21 October 2026, night: "raise the utility and reimbursement caps to 60 cents, with a second confirmation
  // above 40". A flat per-kWh rate above 40 that is not the one already saved shows the second step before it saves.
  const needsConfirmStep = needsRateConfirmStep(flatRateCents, electricityRateType,
    { cents: location.electricity_rate_cents, rateType: location.electricity_rate_type });
  const [confirmCents, setConfirmCents] = useState<number | null>(null);
  const confirmStepShown = needsConfirmStep && confirmCents === flatRateCents;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit(false);
  };

  const submit = (confirmed: boolean) => {
    // Heal coordinates before sending. If the DB value came in mangled
    // (decimal-stripped longitude from the unresolved upstream write bug),
    // the form would otherwise round-trip the bad value to PUT and the
    // server's validateCoordinates would 400 — blocking every unrelated
    // edit (name, photo, subtitle, etc.) until the data was hand-fixed.
    // Normalizing on submit means the first save after this ships also
    // silently heals the stored record.
    //
    // When normalizeLatLng returns null (input was totally absent), we
    // omit the coordinates field entirely rather than sending
    // `undefined` — omitted = "don't update," which preserves whatever
    // the DB has instead of clobbering it to null.
    const normalized = normalizeLatLng(
      location.address.coordinates ?? null,
      `location ${location._id}`,
    );
    const addressPatch = {
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim(),
      country: country.trim() || 'US',
      ...(normalized ? { coordinates: normalized } : {}),
    };
    // Three-mode electricity reimbursement save. Whichever mode is
    // active gets its value persisted; the OTHER two modes get
    // explicitly nulled out so the server-side resolver (tariff
    // > inline TOU > flat) reads unambiguously and the audit row
    // captures a clean previous → new transition. The flat rate
    // ALWAYS ships (defaulting to 0 when unset) since the server
    // schema requires a number — it's the floor of the resolver
    // chain. flatRateCents is the canonical-cents value derived above.
    // Save is disabled while any rate is over the ceiling; this guard
    // covers an Enter-key submit.
    if (overCeiling || zoneMissing) return;
    // The second step first; its Confirm comes back here confirmed.
    if (needsConfirmStep && !confirmed) { setConfirmCents(flatRateCents); return; }

    const tariffPatch: Partial<{
      electricity_tariff_id: string | null;
      electricity_tou_windows: ElectricityTouWindowAdmin[] | null;
      electricity_rate_cents: number;
      electricity_rate_type: 'per_kwh' | 'per_hour';
    }> = {};
    // Flat-branch unit selector applies regardless of mode — it
    // governs the fallback rate used when TOU/Tariff resolution
    // doesn't match a window. Persist it on every save so a
    // round-trip preserves the operator's intent.
    tariffPatch.electricity_rate_type = electricityRateType;
    if (reimbursementMode === 'tariff') {
      const trimmedTariff = electricityTariffId.trim();
      tariffPatch.electricity_tariff_id = trimmedTariff === '' ? null : trimmedTariff;
      tariffPatch.electricity_tou_windows = null;
      tariffPatch.electricity_rate_cents = flatRateCents;
    } else if (reimbursementMode === 'inline_tou') {
      tariffPatch.electricity_tariff_id = null;
      tariffPatch.electricity_tou_windows = inlineTouWindows;
      tariffPatch.electricity_rate_cents = flatRateCents;
    } else {
      // 'flat' — null both the tariff and the inline-TOU paths.
      tariffPatch.electricity_tariff_id = null;
      tariffPatch.electricity_tou_windows = null;
      tariffPatch.electricity_rate_cents = flatRateCents;
    }

    void onSave({
      name: name.trim(),
      address: addressPatch as Location['address'],
      type,
      is_active: isActive,
      is_visible_on_driver_map: isVisibleOnDriverMap,
      photo_url: photoUrl.trim() === '' ? null : photoUrl.trim(),
      subtitle: subtitle.trim() === '' ? null : subtitle.trim(),
      ...tariffPatch,
      ...(timezone !== (location.timezone ?? '') ? { timezone: timezone === '' ? null : timezone } : {}),
      ...(confirmed && needsConfirmStep ? { confirm_above_40: true } : {}),
    });
  };

  const handleRevert = () => {
    setPhotoUrl('');
    setSubtitle('');
  };

  return (
    <form className={styles.editCard} onSubmit={handleSubmit}>
      <div className={styles.editTitleRow}>
        <p className={styles.editTitle}>Edit location</p>
      </div>
      {error && <div className={formStyles.formError}>{error}</div>}

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Name</label>
        <input className={formStyles.input} value={name} onChange={(e) => setName(e.target.value)} required />
      </div>

      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Street</label>
        <input className={formStyles.input} value={street} onChange={(e) => setStreet(e.target.value)} required />
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>City</label>
          <input className={formStyles.input} value={city} onChange={(e) => setCity(e.target.value)} required />
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>State</label>
          <input className={formStyles.input} value={state} onChange={(e) => setState(e.target.value)} required />
        </div>
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>ZIP</label>
          <input className={formStyles.input} value={zip} onChange={(e) => setZip(e.target.value)} required />
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Country</label>
          <input className={formStyles.input} value={country} onChange={(e) => setCountry(e.target.value)} />
        </div>
      </div>

      {/* ── Hero photo + subtitle card ─────────────────────────────────
          Drives the shared StationHero component on the driver-facing
          station detail + pre-session screens. Priority chain:
            1. photoUrl (this input) — admin override
            2. Street View Static at the location's coordinates
            3. Brand gradient tile
          The live preview below mirrors the chain so the admin sees
          exactly what the driver will see. "Revert" clears both fields
          back to the Street View / gradient fallback. */}
      <div className={styles.heroCard}>
        <div className={styles.heroPreview}>
          <HeroPreview
            photoUrl={photoUrl}
            coordinates={location.address.coordinates ?? null}
          />
        </div>
        <div className={styles.heroInputs}>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Hero photo URL</label>
            <input
              className={formStyles.input}
              type="url"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              placeholder="https://…   (leave blank for Street View auto-capture)"
              maxLength={2048}
            />
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Subtitle</label>
            <input
              className={formStyles.input}
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="MISSION HILLS HUB   (leave blank for single-tier title)"
              maxLength={120}
            />
          </div>
          <div className={styles.heroActions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={handleRevert}
              disabled={!photoUrl && !subtitle}
            >
              Revert to default
            </button>
          </div>
        </div>
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Type</label>
          <select className={formStyles.input} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="residential">Residential</option>
            <option value="multifamily">Multi-Family</option>
            <option value="commercial">Commercial</option>
            <option value="public">Public</option>
            <option value="fleet">Fleet</option>
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Active</label>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label={isActive ? 'Operational: part of the fleet' : 'Inactive: hidden everywhere'}
          />
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>Driver map visibility</label>
          <Switch
            checked={isVisibleOnDriverMap}
            onChange={(e) => setIsVisibleOnDriverMap(e.target.checked)}
            disabled={!isActive}
            label={isVisibleOnDriverMap && isActive
              ? 'Visible to drivers on the PWA map'
              : !isActive
                ? 'Not visible (location is inactive)'
                : 'Hidden from driver discovery'}
          />
        </div>
      </div>

      <p className={styles.hint}>
        Changing the address doesn't re-geocode. If you move the pin, update coordinates via the API.
      </p>

      {/* ── Electricity Reimbursement (three-mode commit) ──────────
          The payout calculator subtracts an electricity-cost leg
          from each session's gross. Three mutually-exclusive
          modes supply the rate; the resolver picks them in this
          priority on read:
            1. tariff:     electricity_tariff_id → Tariff TOU
            2. inline_tou: electricity_tou_windows[] → inline TOU
            3. flat:       electricity_rate_cents (the floor)
          The active mode's fields persist on Save; the others
          get nulled out so the server-side resolver reads
          unambiguously and the audit row captures a clean
          mode-transition pair. */}
      <div className={styles.electricityCard}>
        <p className={styles.electricityCardTitle}>Electricity Reimbursement</p>
        <p className={styles.electricityCardLead}>
          What this building actually pays per kWh. Subtracted from each
          session's gross before the platform + owner split.
        </p>

        {/* Mode picker — radio-style segmented control. Hides the
            inactive panes so the form always shows one source of
            truth at a time. */}
        <div className={styles.reimbursementModes} role="tablist" aria-label="Reimbursement mode">
          {([
            { id: 'tariff', label: 'Use a saved tariff' },
            { id: 'inline_tou', label: 'Custom TOU schedule' },
            { id: 'flat', label: 'Flat rate' },
          ] as const).map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={reimbursementMode === m.id}
              className={
                reimbursementMode === m.id
                  ? styles.reimbursementModeActive
                  : styles.reimbursementMode
              }
              onClick={() => setReimbursementMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {reimbursementMode === 'tariff' && (
          <div className={formStyles.field}>
            <label className={formStyles.label}>Electricity tariff (TOU)</label>
            <select
              className={formStyles.input}
              value={electricityTariffId}
              onChange={(e) => setElectricityTariffId(e.target.value)}
            >
              <option value="">— Choose a tariff —</option>
              {tariffs.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.name} {t.type === 'time_of_use' ? '· TOU' : t.type === 'tiered' ? '· Tiered' : '· Flat'}
                </option>
              ))}
              {/* If the location has a tariff configured but the
                  list hasn't loaded yet, fall back to a synthetic
                  option so the dropdown doesn't blank out the
                  current selection visually. */}
              {electricityTariffId && !tariffs.some((t) => t._id === electricityTariffId) && (
                <option value={electricityTariffId}>(current tariff)</option>
              )}
            </select>
            {/* Tariff rates are cents (the Tariffs page edits them in ¢).
                The cap named is the one the offending rate answers to: 40¢
                for a per-kWh base or tier, 80¢ for a TOU row or any rate
                on a per-hour tariff. */}
            {tariffOverRate && (
              <p className={formStyles.errorMsg} role="alert">
                This tariff has a rate above {ceilingLabel('cents', tariffOverRate.maxCents)} ({tariffOverRate.path}).{' '}
                {ceilingMessageFor('cents', tariffOverRate.maxCents)}
              </p>
            )}
            <p className={styles.electricityHint}>
              Session-start pins the matching window&rsquo;s rate onto
              the Session row. Edit the tariff itself on the Tariffs
              page.
            </p>

            {/* Read-only TOU summary when the selected tariff IS a
                TOU tariff. Renders via the shared TimeWindowRows in
                disabled mode so the admin sees the schedule
                without an inline editor here. */}
            {selectedTariff && selectedTariff.type === 'time_of_use'
              && selectedTariff.rates.time_of_use_schedule
              && selectedTariff.rates.time_of_use_schedule.length > 0 && (
              <div className={styles.touSummary}>
                <p className={styles.touSummaryTitle}>TOU schedule (read-only)</p>
                <TimeWindowRows<{ day_of_week: number[]; start_hour: number; end_hour: number; energy_rate: number }>
                  windows={selectedTariff.rates.time_of_use_schedule.map((w) => ({ ...w }))}
                  onChange={() => undefined}
                  getDays={(w) => w.day_of_week}
                  setDays={(w, days) => ({ ...w, day_of_week: days })}
                  getStartHour={(w) => w.start_hour}
                  setStartHour={(w, h) => ({ ...w, start_hour: h })}
                  getEndHour={(w) => w.end_hour}
                  setEndHour={(w, h) => ({ ...w, end_hour: h })}
                  createEmpty={() => ({ day_of_week: [], start_hour: 0, end_hour: 24, energy_rate: 0 })}
                  extraColumn={{
                    label: 'Rate (¢/kWh)',
                    render: (row) => (
                      <input
                        className={formStyles.input}
                        type="number"
                        value={row.energy_rate}
                        readOnly
                        tabIndex={-1}
                      />
                    ),
                  }}
                  addButtonText="Edit on Tariffs page to add"
                  disabled
                />
              </div>
            )}
          </div>
        )}

        {reimbursementMode === 'inline_tou' && (
          <div className={formStyles.field}>
            <label className={formStyles.label}>Custom TOU schedule</label>
            <p className={styles.electricityHint}>
              Define windows directly on this Location without
              creating a Tariff doc. Each row sets a rate AND a unit
              (per kWh or per hour). Per-hour windows enable
              idle-fee / time-billed reimbursement. Load a preset to
              start from a real utility&rsquo;s schedule shape, then
              edit. Server rejects overlapping intervals on save.
            </p>
            <LocationTouPicker
              value={inlineTouWindows}
              onChange={setInlineTouWindows}
            />
            {inlineTouWindows.length === 0 && (
              <p className={styles.electricityHint}>
                Add at least one window before saving, or switch
                to Flat rate.
              </p>
            )}
          </div>
        )}

        {reimbursementMode === 'flat' && (
          <div className={formStyles.field}>
            <label className={formStyles.label}>
              Flat rate ({(() => {
                const sym = flatDisplayUnit === 'dollars' ? '$' : '¢';
                const denom = electricityRateType === 'per_hour' ? 'hour' : 'kWh';
                return `${sym}/${denom}`;
              })()})
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className={formStyles.input}
                type="number"
                min={0}
                // The flat rate's cap in the display unit: 40 / 0.40 per
                // kWh, 80 / 0.80 per hour. flatOverCeiling tests the same
                // unrounded value against the same cap.
                max={ceilingInUnit(flatDisplayUnit, flatMaxCents)}
                aria-invalid={flatOverCeiling || undefined}
                aria-describedby={flatOverCeiling ? 'location-flat-rate-ceiling-error' : undefined}
                // step granularity follows the display unit: 0.0001 for
                // dollars (so cents resolution survives — 18¢ = $0.18),
                // 0.01 for cents (the legacy precision).
                step={flatDisplayUnit === 'dollars' ? '0.0001' : '0.01'}
                placeholder={flatDisplayUnit === 'dollars' ? 'e.g. 0.1800' : 'e.g. 15'}
                style={{ flex: '1 1 140px', minWidth: 140 }}
                value={electricityRateCents}
                onChange={(e) => setElectricityRateCents(e.target.value)}
              />
              {/* 4-option combined dropdown — encodes BOTH the display
                  unit (cents vs dollars) AND the rate_type
                  (per_kwh vs per_hour) in a single selector. Operators
                  think of the unit as one choice ("$ per kWh"); splitting
                  into two dropdowns reads heavier than the gain. */}
              <select
                className={formStyles.input}
                style={{ flex: '0 1 130px', minWidth: 130 }}
                value={flatCombinedUnit}
                onChange={(e) => handleFlatUnitChange(e.target.value as FlatRateUnit)}
                aria-label="Flat rate unit"
              >
                <option value="cents_per_kwh">¢/kWh</option>
                <option value="dollars_per_kwh">$/kWh</option>
                <option value="cents_per_hour">¢/hour</option>
                <option value="dollars_per_hour">$/hour</option>
              </select>
            </div>
            {/* Worded for the unit and cap being typed: "60¢" in ¢/kWh,
                "$0.60 (enter 0.60 or less)" in $/kWh, "80¢" and "$0.80"
                per hour. */}
            {flatOverCeiling && (
              <p id="location-flat-rate-ceiling-error" className={formStyles.errorMsg} role="alert">
                {ceilingMessageFor(flatDisplayUnit, flatMaxCents)}
              </p>
            )}
            <p className={styles.electricityHint}>
              Single rate applied to every session, regardless of
              time-of-day. Also the floor for the other modes when
              no window matches at session-start.
            </p>
          </div>
        )}

        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="location-timezone">Time zone</label>
          <select id="location-timezone" className={formStyles.input} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <option value="">Not set</option>
            {timeZoneOptions(timezone || location.timezone).map((z) => <option key={z.zone} value={z.zone}>{z.label}</option>)}
          </select>
          <p className={styles.electricityHint}>
            Time-of-use periods are read in this time zone. A time-of-use rate needs one.
            {zoneSuggestion && timezone !== zoneSuggestion && <>
              {' '}From the address: {zoneSuggestion}.{' '}
              <button type="button" className={formStyles.btnSecondary} onClick={() => setTimezone(zoneSuggestion)}>Use it</button>
            </>}
          </p>
          {zoneMissing && (
            <p className={formStyles.errorMsg} role="alert">
              Set this location&rsquo;s time zone before saving a time-of-use electricity rate.
            </p>
          )}
          {zoneState === 'warn' && (
            <p className={styles.electricityWarning}>
              This location&rsquo;s time-of-use rate has no time zone, so its electricity reimbursement goes to review. Set one.
            </p>
          )}
        </div>

        {/* Warning when the active mode produces no usable rate */}
        {((reimbursementMode === 'tariff' && !electricityTariffId.trim())
          || (reimbursementMode === 'inline_tou' && inlineTouWindows.length === 0)
          || (reimbursementMode === 'flat' && (electricityRateCents.trim() === '' || parseFloat(electricityRateCents) === 0))
        ) && (
          <p className={styles.electricityWarning}>
            No electricity reimbursement configured — payouts will
            treat this building&rsquo;s electricity cost as $0.
          </p>
        )}

        {/* The flat rate ships in every mode, so one above the ceiling
            (typed, or stored before the ceiling existed) blocks Save
            even while another mode is showing. Its only editor is the
            Flat rate tab, and saving from that tab drops the tariff /
            schedule, so the copy says to come back before saving. */}
        {flatOverCeiling && reimbursementMode !== 'flat' && (
          <p className={formStyles.errorMsg} role="alert">
            The flat fallback rate is over the {ceilingLabel(flatDisplayUnit, flatMaxCents)} limit, so
            this can&rsquo;t be saved yet. Set it to {ceilingLabel(flatDisplayUnit, flatMaxCents)} or less
            on the Flat rate tab, then switch back
            to {reimbursementMode === 'tariff' ? 'Use a saved tariff' : 'Custom TOU schedule'} before
            you save. Saving from the Flat rate tab removes
            the {reimbursementMode === 'tariff' ? 'saved tariff' : 'custom schedule'} from this location.
          </p>
        )}
      </div>

      {confirmStepShown && (
        <RateConfirmStep cents={flatRateCents} busy={saving} buttonClassName={formStyles.btnPrimary} cancelClassName={formStyles.btnSecondary}
          onConfirm={() => submit(true)} onCancel={() => setConfirmCents(null)} />
      )}

      <div className={styles.editActions}>
        <button type="button" className={formStyles.btnSecondary} onClick={onCancel} disabled={saving}>
          <X size={13} /> Cancel
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={saving || overCeiling || zoneMissing || confirmStepShown || !name || !street || !city || !state || !zip}>
          {saving ? 'Saving…' : <><Check size={13} /> Save changes</>}
        </button>
      </div>
    </form>
  );
}

// ── Hero preview ─────────────────────────────────────────────────────────
//
// Mirrors the priority chain the shared StationHero component (commit 2
// of this ticket) will use at render time:
//
//   1. photoUrl non-empty                  → <img src={photoUrl}>
//   2. coordinates + VITE_GOOGLE_MAPS_KEY  → Street View Static
//   3. fallback                            → brand gradient tile
//
// Inlined here because commit 2 hasn't shipped yet; once StationHero
// lands, this component collapses to `<StationHero ... height={80} />`
// and the URL construction logic moves upstream.

const GOOGLE_MAPS_KEY = import.meta.env['VITE_GOOGLE_MAPS_KEY'] as string | undefined;

function buildStreetViewUrl(
  coordinates: { lat: number; lng: number } | null,
  apiKey: string | undefined,
): string | null {
  if (!coordinates || !apiKey) return null;
  const params = new URLSearchParams({
    size: '640x320',
    scale: '2',
    fov: '80',
    pitch: '0',
    location: `${coordinates.lat},${coordinates.lng}`,
    return_error_code: 'true',
    key: apiKey,
  });
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}

// Brand gradient tile used when neither override nor Street View is
// available. Matches the PreSessionPage + StationDetailPage fallback
// so the preview never misrepresents the driver's experience.
const HERO_GRADIENT =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='320' viewBox='0 0 800 320'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%2321c163' stop-opacity='0.4'/%3E%3Cstop offset='100%25' stop-color='%230a0a0a' stop-opacity='0.9'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='320' fill='url(%23g)'/%3E%3C/svg%3E";

function HeroPreview({
  photoUrl,
  coordinates,
}: {
  photoUrl: string;
  coordinates: { lat: number; lng: number } | null;
}) {
  // onError-driven fallback: if the admin's pasted photoUrl 404s or
  // Street View returns a 4xx (no coverage, bad key), we swap to the
  // next tier without requiring a pre-flight request.
  const [imgError, setImgError] = useState<{ photoUrl: boolean; streetView: boolean }>({
    photoUrl: false, streetView: false,
  });

  // Reset error state when the input changes so a corrected URL re-tries.
  useEffect(() => {
    setImgError({ photoUrl: false, streetView: false });
  }, [photoUrl]);

  const trimmed = photoUrl.trim();
  const streetViewUrl = buildStreetViewUrl(coordinates, GOOGLE_MAPS_KEY);

  let src: string;
  let tier: 'upload' | 'streetview' | 'gradient';
  if (trimmed && !imgError.photoUrl) {
    src = trimmed;
    tier = 'upload';
  } else if (streetViewUrl && !imgError.streetView) {
    src = streetViewUrl;
    tier = 'streetview';
  } else {
    src = HERO_GRADIENT;
    tier = 'gradient';
  }

  return (
    <div className={styles.heroPreviewInner}>
      <img
        key={src}
        src={src}
        alt="Hero preview"
        onError={() => {
          if (tier === 'upload') setImgError((s) => ({ ...s, photoUrl: true }));
          else if (tier === 'streetview') setImgError((s) => ({ ...s, streetView: true }));
        }}
      />
      <span className={styles.heroPreviewTier}>
        {tier === 'upload' ? 'Uploaded URL'
          : tier === 'streetview' ? 'Street View'
            : 'Gradient fallback'}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Per-Location Join Code card (per-Location join codes commit)
// ══════════════════════════════════════════════════════════════════════════
//
// Twin of the Tenant-tier `BuildingJoinCodeCard` on SettingsPage. The
// state machine and behavior match — display + Copy/Regenerate/Clear
// when a code exists, big "Generate code" CTA when empty, lazy-fetched
// usage counter underneath.
//
// `initialJoinCode` / `initialGeneratedAt` come from the parent's
// already-fetched LocationDetail so we don't double-fetch on mount.
// `onChanged` lets the parent keep its cached LocationDetail in sync
// after generate / clear (so the inline edit form, etc., snapshots
// fresh code state).

interface LocationJoinCodeCardProps {
  locationId: string;
  /** The building's tenant: a tenant_admin may rotate only its own tenant's codes. */
  locationTenantId: string;
  initialJoinCode: string | null;
  initialGeneratedAt: string | null;
  onChanged?: (code: string | null, generatedAt: string | null) => void;
}

function buildLocationJoinUrl(code: string | null): string | null {
  if (!code) return null;
  // PWA origin. In production this is `app.evinitiative.com`; in
  // dev it falls back to the current origin (the same logic the
  // server uses for the share-URL field). Mirror it client-side
  // so the card works without a live server response.
  const base = (import.meta.env['VITE_PWA_ORIGIN'] as string | undefined) ?? window.location.origin;
  return `${base.replace(/\/$/, '')}/join?code=${encodeURIComponent(code)}`;
}

function formatLocationDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function LocationJoinCodeCard({
  locationId,
  locationTenantId,
  initialJoinCode,
  initialGeneratedAt,
  onChanged,
}: LocationJoinCodeCardProps) {
  // Generate / Regenerate / Clear mirror the server's gate on the join-code
  // routes (admin/locations.ts: requireUnscopedPersona, then super_admin or
  // tenant_admin): a viewer, an operator or a Site persona saw all three and
  // got a 403. Same shape as MoneyPage's requireTenantScope mirror - a
  // templated persona's tenant_admin claim does not count - plus the route's
  // own-tenant clause (a residential home read through the operator-homes
  // scope belongs to another tenant).
  const user = useAuthStore((st) => st.user);
  const canRotate = user?.role === 'super_admin'
    || (user?.role === 'tenant_admin' && !user?.template && user?.tenant_id === locationTenantId);
  const [joinCode, setJoinCode] = useState<string | null>(initialJoinCode);
  const [generatedAt, setGeneratedAt] = useState<string | null>(initialGeneratedAt);
  const [usageCount, setUsageCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Lazy-fetch the usage stat. Don't block the rest of the page on
  // it — the count is a nice-to-have, not a render gate.
  useEffect(() => {
    let cancelled = false;
    fetchLocationJoinCodeUsage(locationId)
      .then((u) => { if (!cancelled) setUsageCount(u.joined_via_code_count); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [locationId]);

  const refreshUsage = (): void => {
    fetchLocationJoinCodeUsage(locationId)
      .then((u) => setUsageCount(u.joined_via_code_count))
      .catch(() => undefined);
  };

  const handleGenerate = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await regenerateLocationJoinCode(locationId);
      setJoinCode(result.join_code);
      setGeneratedAt(result.join_code_generated_at ?? null);
      onChanged?.(result.join_code, result.join_code_generated_at ?? null);
      refreshUsage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate code');
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async (): Promise<void> => {
    if (!confirm('Invalidate the current join code? Existing /join links will stop working immediately.')) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      await clearLocationJoinCode(locationId);
      setJoinCode(null);
      setGeneratedAt(null);
      onChanged?.(null, null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear code');
    } finally {
      setBusy(false);
    }
  };

  const joinUrl = buildLocationJoinUrl(joinCode);

  const handleCopy = (): void => {
    if (!joinUrl) return;
    void navigator.clipboard.writeText(joinUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }).catch(() => undefined);
  };

  return (
    <section className={styles.joinCard}>
      <div className={styles.joinCardTitleRow}>
        <h2 className={styles.joinCardTitle}>
          <KeyRound size={14} />
          Building Join Code
        </h2>
      </div>

      <p className={styles.joinCardLead}>
        Per-Location code for residents of this building. Distinct from any
        Tenant-level code on the parent operator. Anyone with the link can
        register or switch their account into this Location.
      </p>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {joinCode ? (
        <div className={styles.joinCodeRow}>
          <div className={styles.joinCodeBlock}>
            <span className={styles.joinCodeLabel}>Active code</span>
            <span className={styles.joinCodeValue}>{joinCode}</span>
            {generatedAt && (
              <span className={styles.joinCodeMeta}>
                Generated {formatLocationDate(new Date(generatedAt))}
              </span>
            )}
          </div>
          <div className={styles.joinCodeActions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={handleCopy}
              disabled={busy || !joinUrl}
            >
              {copied
                ? <><Check size={13} /> Copied</>
                : <><Copy size={13} /> Copy join URL</>}
            </button>
            {canRotate && (
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={handleGenerate}
                disabled={busy}
              >
                {busy
                  ? <><Loader2 size={13} className={styles.joinSpin} /> Working…</>
                  : <><RefreshCw size={13} /> Regenerate</>}
              </button>
            )}
            {canRotate && (
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={handleClear}
                disabled={busy}
                title="Invalidate the current code"
              >
                <Trash2 size={13} /> Clear code
              </button>
            )}
          </div>
          {joinUrl && (
            <p className={styles.joinUrl}>{joinUrl}</p>
          )}
        </div>
      ) : (
        <div className={styles.joinCodeEmpty}>
          <p className={styles.joinCodeEmptyText}>
            {canRotate
              ? 'No code set. Generate one to start sharing.'
              : 'No code set. EV Initiative can generate one for this building.'}
          </p>
          {canRotate && (
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={handleGenerate}
              disabled={busy}
            >
              {busy
                ? <><Loader2 size={13} className={styles.joinSpin} /> Generating…</>
                : <><KeyRound size={13} /> Generate code</>}
            </button>
          )}
        </div>
      )}

      {usageCount !== null && (
        <p className={styles.joinUsage}>
          {usageCount === 0
            ? 'No users have joined via this code yet.'
            : `${usageCount} user${usageCount === 1 ? '' : 's'} joined via this code.`}
        </p>
      )}
    </section>
  );
}
